import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogWrapper } from "../common/dialogWrapper";
import { Box, Stack } from "@mui/material";
import { TextWrapper } from "../formUtilities/useTextWrapper";
import { FormikProvider, useFormik } from "formik";
import { ContactUSSchema } from "../../validations/schema";
import { FormHelper } from "../../../helpers/formHelper";
import { InputWrapper } from "../formUtilities/useInput";
import { InternationalPhone } from "../formUtilities/useInternationalPhone";
import { FsButton } from "../button";
import { UserService } from "../../../services/userService";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import { IS_SA } from "../../../config/appConfig";
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';

export const ContactUsDialog = ({ state = false, onClose = () => { } }) => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const userService = new UserService();

  const form = useFormik({
    initialValues: { fullName: "", contactNumber: '', emailAddress: '', message: '' },
    validationSchema: ContactUSSchema,
    onSubmit: () => {
      onMessage();
    },
    ...FormHelper.formikProps()
  });

  const onMessage = () => {
    setLoading(true);
    let formValue = form.values;
    formValue = {
      ...formValue,
      contactNumber: `+${formValue?.contactNumber?.code} ${formValue?.contactNumber?.number}`
    };
    userService.sendMessageContactUs(formValue).then(resp => {
      setIsSuccess(true);
      setLoading(false);
    }).catch(err => {
      setLoading(false);
    });
  };

  const getDirection = () => {
    if (!IS_SA) {
      return 'https://maps.app.goo.gl/etnEJYtuoBwSBKZ29';
    }
    return 'https://maps.app.goo.gl/fB5Heqj4Fart34fH9';
  };

  const SuccessSection = () => {
    return <Box className='flex flex-col justify-center items-center'>
      <Box className='invest-success mb-6'>
        <svg
          viewBox="0 0 26 26"
          xmlns="http://www.w3.org/2000/svg">
          <g
            stroke="currentColor"
            stroke-width="2"
            fill="none"
            fill-rule="evenodd"
            stroke-linecap="round"
            stroke-linejoin="round">
            <path
              class="circle"
              d="M13 1C6.372583 1 1 6.372583 1 13s5.372583 12 12 12 12-5.372583 12-12S19.627417 1 13 1z"
            />
            <path
              class="tick"
              d="M6.5 13.5L10 17 l8.808621-8.308621"
            />
          </g>
        </svg>
      </Box>
      <TextWrapper
        classList="!font-bold !text-primary !text-xl !mb-3"
        text={t('thankYou')}></TextWrapper>
      <TextWrapper
        classList="!text-sm !text-center"
        text={t('contactUsSuccess')}></TextWrapper>
      <Box className='mt-6'>
        <FsButton
          type="button"
          onClick={() => {
            onClose();
          }}>{t('close')}</FsButton>
      </Box>
    </Box>;
  };


  return <DialogWrapper
    maxWidth={!isSuccess ? 'md' : 'xs'}
    state={state}
    title={!isSuccess ? t('sendUsMessage') : t('messageReceived')}
    subTitle={!isSuccess ? t('contactUsInfo') : ''}
    onClose={() => {
      onClose(false);
    }}
    dialogBodyClass={!isSuccess ? '!p-0' : ''}
    content={
      !isSuccess ?
        <Box className='grid  md:grid-cols-3 grid-cols-1 md:gap-4 gap-6'>
          <Box className='md:col-span-2 p-6 md:!pt-6 !pt-0 md:order-1 order-2'>
            <Stack
              gap={2}
              className="text-start">
              <form>
                <FormikProvider value={form}>
                  <Box className='grid grid-flow-row gap-4'>
                    <Box className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <InputWrapper
                        key={'fullName'}
                        name={'fullName'}
                        label={t('fullName')}></InputWrapper>
                      <InternationalPhone
                        name={'contactNumber'}
                        key={'contactNumber'}
                        label={'contactNumber'}></InternationalPhone>
                    </Box>
                    <InputWrapper
                      key={'emailAddress'}
                      name={'emailAddress'}
                      label={t('emailAddress')}></InputWrapper>
                    <InputWrapper
                      isTextArea
                      rows={3}
                      key={'message'}
                      name={'message'}
                      label={t('message')}></InputWrapper>
                  </Box>
                </FormikProvider>
              </form>
            </Stack>
            <Box className="!w-full text-center !mt-4">
              <FsButton
                loading={loading}
                type="button"
                onClick={() => {
                  form.submitForm();
                }}>{t('sendMessage')}</FsButton>
            </Box>
          </Box>
          <Box className='p-6 md:order-2 order-1 primary-bg text-white'>
            <Box className="flex flex-col flex-0 items-start">
              <TextWrapper
                classList="!font-bold !text-xl !text-white !mb-1"
                text={t('contactInformation')}></TextWrapper>
              <TextWrapper
                classList="!sub-title-text !text-white"
                text={t('contactInformationInfo')}></TextWrapper>
              <Box>
                <Box className='flex flex-col'>
                  <Box className='flex flex-row items-start !text-sm mt-4 cursor-pointer'>
                    <LocationOnIcon className="!text-[24px]" />
                    <a href={getDirection()} target="_blank" rel="noopener noreferrer" className='mx-2 !text-white !text-start !text-sm !font-[500] underline hover:no-underline'>{t('contactAddressText')}</a>
                  </Box>
                </Box>
                <Box className='flex flex-row items-center !text-sm mt-8'>
                  <EmailIcon className="!text-[24px]" />
                  <a href="mailto:support@fundingsouq.com"
                    className="mx-2 !text-white !text-sm !font-[500] underline hover:no-underline" rel="nofollow">
                    support@fundingsouq.com
                  </a>
                </Box>
                <Box className='flex flex-row items-center !text-sm mt-8'>
                  <LocalPhoneIcon className="!text-[24px]" />
                  {IS_SA ? <a href="tel:+9668001240366"
                    className="mx-2 !text-white !text-sm !font-[500] underline hover:no-underline" rel="nofollow">
                    +966 8001240366
                  </a> : <a href="tel:+971525255050"
                    className="mx-2 !text-white !text-sm !font-[500] underline hover:no-underline" rel="nofollow">
                    +971 525255050
                  </a>}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box> : <SuccessSection />
    }
  ></DialogWrapper>;
};